import React, { useCallback, useRef, useEffect, useState } from "react";
import { Box, Divider, Stack, Typography, CircularProgress, Collapse } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { StyledMenu } from "./styles";
import { HubIcon } from "../../HubIcon";
import HubContextMenuItem from "../../HubContextMenu/HubContextMenuItem";
import SearchResultsEmpty from "./SearchResultsEmpty";
import { useLoadMoreCategory } from "../../../hooks";

const TitleResult = ({ icon, title, totalCount, currentCount, isOpen, onToggle }) => (
  <Stack 
    direction="row" 
    gap="10px" 
    p="12px" 
    alignItems="center" 
    justifyContent="space-between"
    sx={{ 
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "action.hover",
      },
    }}
    onClick={onToggle}
  >
    <Stack direction="row" gap="10px" alignItems="center">
      <HubIcon icon={isOpen ? "ChevronDown" : "ChevronRight"} size={12} />
      {icon && <HubIcon icon={icon} size={12} />}
      <Typography
        variant="boldCaption"
        sx={{
          fontWeight: 500,
          textTransform: "none",
        }}
      >
        {title}
      </Typography>
    </Stack>
    {totalCount > 0 && (
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontSize: "10px",
        }}
      >
        ({currentCount} of {totalCount})
      </Typography>
    )}
  </Stack>
);

const SearchResults = ({
  data,
  onClose = () => {},
  customOnClick,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  searchQuery,
}) => {
  const navigate = useNavigate();
  const observerTarget = useRef(null);
  const [categorySkips, setCategorySkips] = useState({});
  const [additionalCategoryResults, setAdditionalCategoryResults] = useState({});
  const [loadingCategory, setLoadingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  const { mutateAsync: loadMoreCategory } = useLoadMoreCategory();

  // Reset category-specific state when search query changes
  useEffect(() => {
    setCategorySkips({});
    setAdditionalCategoryResults({});
    setLoadingCategory(null);
  }, [searchQuery]);

  const onClick = (e, item) => {
    if (customOnClick) {
      customOnClick(item);
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    if (item?.path) {
      navigate(`${item?.path}/${item?.id}`);
      onClose();
    }
  };

  // Flatten all pages of results into a single array
  const allResults = React.useMemo(() => {
    if (!data?.pages) return [];
    
    const mergedCategories = {};
    
    // Merge results from infinite query pages
    data.pages.forEach((page) => {
      page.results?.forEach((category) => {
        if (!mergedCategories[category.title]) {
          mergedCategories[category.title] = { ...category, results: [] };
        }
        mergedCategories[category.title].results.push(...category.results);
        mergedCategories[category.title].totalCount = category.totalCount;
        mergedCategories[category.title].hasMore = category.hasMore;
      });
    });
    
    // Merge additional per-category results
    Object.entries(additionalCategoryResults).forEach(([title, data]) => {
      if (mergedCategories[title]) {
        mergedCategories[title].results.push(...data.results);
        mergedCategories[title].hasMore = data.hasMore;
      }
    });
    
    return Object.values(mergedCategories);
  }, [data?.pages, additionalCategoryResults]);

  const hasMultipleCategories = allResults.length > 1;

  // Initialize all categories as expanded
  useEffect(() => {
    if (allResults.length > 0) {
      const initialExpanded = {};
      allResults.forEach(r => {
        // Set new categories to expanded, keep existing state for others
        initialExpanded[r.title] = expandedCategories[r.title] !== undefined 
          ? expandedCategories[r.title] 
          : true;
      });
      setExpandedCategories(initialExpanded);
    }
  }, [allResults.map(r => r.title).join(',')]);

  const toggleCategory = (categoryTitle) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryTitle]: !prev[categoryTitle],
    }));
  };

  // Handle loading more for a specific category
  const handleLoadMoreCategory = async (categoryTitle) => {
    const categoryKey = getCategoryKey(categoryTitle);
    const currentSkip = categorySkips[categoryKey] || 10; // Start at 10 since we already have first 10
    
    setLoadingCategory(categoryKey);
    
    try {
      const result = await loadMoreCategory({
        searchQuery,
        category: categoryKey,
        skip: currentSkip,
      });
      
      if (result.results && result.results.length > 0) {
        const categoryResult = result.results[0];
        
        // Store additional results in state
        setAdditionalCategoryResults(prev => ({
          ...prev,
          [categoryTitle]: {
            results: [...(prev[categoryTitle]?.results || []), ...categoryResult.results],
            hasMore: categoryResult.hasMore,
          }
        }));
        
        setCategorySkips({ ...categorySkips, [categoryKey]: currentSkip + 10 });
      }
    } catch (error) {
      console.error('Error loading more:', error);
    } finally {
      setLoadingCategory(null);
    }
  };

  // Map category titles to API keys
  const getCategoryKey = (title) => ({
    'Customers': 'customers',
    'Orders': 'orders',
    'Lot Numbers': 'lotNumbers',
    'Work Orders': 'workOrders',
    'Parts': 'parts',
    'Quotes': 'quotes',
  }[title] || title.toLowerCase());

  // Intersection Observer for infinite scroll (single category only)
  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage && !hasMultipleCategories) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, hasMultipleCategories]
  );

  useEffect(() => {
    if (hasMultipleCategories || !observerTarget.current) return;
    
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.5 });
    observer.observe(observerTarget.current);
    
    return () => observer.disconnect();
  }, [handleObserver, hasMultipleCategories]);

  const hasResults = !!allResults?.length;
  
  return (
    <StyledMenu>
      {hasResults ? (
        <>
          {allResults?.map((r, idx) => (
            <Box key={r.title}>
              <TitleResult 
                icon={r.icon}
                title={r.title}
                totalCount={r.totalCount}
                currentCount={r.results.length}
                isOpen={expandedCategories[r.title]}
                onToggle={() => toggleCategory(r.title)}
              />
              <Collapse in={expandedCategories[r.title]} timeout="auto">
                {r?.results?.map((item, itemIdx) => (
                  <HubContextMenuItem
                    key={`${item.id}-${itemIdx}`}
                    item={{ ...item, icon: "ArrowRight" }}
                    onSelect={onClick}
                  />
                ))}
                
                {/* Load More button for multi-category mode */}
                {hasMultipleCategories && r.hasMore && (
                  <Box
                    sx={{
                      padding: "8px 12px",
                      paddingLeft: "53px",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                    onClick={() => handleLoadMoreCategory(r.title)}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "primary.main",
                        fontWeight: 500,
                      }}
                    >
                      {loadingCategory === getCategoryKey(r.title) 
                        ? "Loading..." 
                        : "Load More"
                      }
                    </Typography>
                  </Box>
                )}
              </Collapse>
              
              {idx < allResults.length - 1 && (
                <Divider orientation="horizontal" flexItem />
              )}
            </Box>
          ))}
          
          {/* Infinite scroll indicator for single category mode */}
          {!hasMultipleCategories && hasNextPage && (
            <Box
              ref={observerTarget}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "16px",
              }}
            >
              {isFetchingNextPage && <CircularProgress size={20} />}
            </Box>
          )}
        </>
      ) : (
        <SearchResultsEmpty />
      )}
    </StyledMenu>
  );
};

export default SearchResults;
